import { useMemo, useState } from 'react'
import { useMqttTopic } from '../mqtt.jsx'
import { useSelection } from '../selection.jsx'
import { sortSessionCodes, isRunPB } from '../sessions.js'
import { fmtSessionTime, riderMatchesQuery } from '../format.js'

const EMPTY_CLASSES = []

export default function AllRunsPage() {
  const results = useMqttTopic('results')
  const { selected, toggle } = useSelection()

  const [classFilter, setClassFilter] = useState(null)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({ key: 'rider', dir: 1 })

  const classes = results?.classes ?? EMPTY_CLASSES

  const allRiders = useMemo(
    () => classes.flatMap((c) => c.competitors.map((r) => ({ ...r, className: c.className }))),
    [classes],
  )

  const sessionCodes = useMemo(() => {
    const codes = new Set()
    allRiders.forEach((r) => Object.keys(r.sessions || {}).forEach((code) => codes.add(code)))
    return sortSessionCodes([...codes])
  }, [allRiders])

  if (!results) {
    return <div className="empty-state">Waiting for results…</div>
  }

  const classNames = [...new Set(allRiders.map((r) => r.className))]

  let rows = allRiders.filter((r) => (!classFilter || r.className === classFilter) && riderMatchesQuery(r, search))

  rows = [...rows].sort((a, b) => {
    let av, bv
    if (sort.key === 'rider') {
      av = a.rider
      bv = b.rider
    } else if (sort.key === 'num') {
      av = a.num
      bv = b.num
    } else {
      av = a.sessions?.[sort.key]?.time ?? null
      bv = b.sessions?.[sort.key]?.time ?? null
    }
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    if (av < bv) return -1 * sort.dir
    if (av > bv) return 1 * sort.dir
    return 0
  })

  const bestBySession = {}
  sessionCodes.forEach((code) => {
    let best = null
    rows.forEach((r) => {
      const t = r.sessions?.[code]?.time
      if (t && (best == null || t < best)) best = t
    })
    bestBySession[code] = best
  })

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))

  return (
    <div>
      <div className="section-head">
        <h2>All Runs</h2>
        <p>Every rider, every session, side by side — click a column to sort by it</p>
      </div>
      <div className="toolbar">
        <div className="search">
          <span aria-hidden="true">⌕</span>
          <input type="text" placeholder="Filter by rider, machine or no.…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="class-filters">
        <button className={`chip ${!classFilter ? 'active' : ''}`} onClick={() => setClassFilter(null)}>
          All
        </button>
        {classNames.map((cls) => (
          <button key={cls} className={`chip ${classFilter === cls ? 'active' : ''}`} onClick={() => setClassFilter(cls)}>
            {cls}
          </button>
        ))}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 30 }} />
              <th className={`num-col ${sort.key === 'num' ? 'sorted' : ''}`} onClick={() => toggleSort('num')}>
                # <span className="arrow">{sort.key === 'num' ? (sort.dir === 1 ? '↑' : '↓') : '↑↓'}</span>
              </th>
              <th className={`rider-col ${sort.key === 'rider' ? 'sorted' : ''}`} onClick={() => toggleSort('rider')}>
                Rider <span className="arrow">{sort.key === 'rider' ? (sort.dir === 1 ? '↑' : '↓') : '↑↓'}</span>
              </th>
              {sessionCodes.map((code) => (
                <th key={code} className={`num-col ${sort.key === code ? 'sorted' : ''}`} onClick={() => toggleSort(code)}>
                  {code} <span className="arrow">{sort.key === code ? (sort.dir === 1 ? '↑' : '↓') : '↑↓'}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={sessionCodes.length + 3}>
                  <div className="empty-state">No riders match this filter.</div>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.num} className={selected.has(r.num) ? 'selected' : ''}>
                <td>
                  <input type="checkbox" className="check" checked={selected.has(r.num)} onChange={() => toggle(r.num)} />
                </td>
                <td className="num-col mono">{r.num}</td>
                <td className="rider">
                  <span className="name">{r.rider}</span>
                  <span className="class-tag">{r.className}</span>
                  <span className="machine">{r.machine}</span>
                </td>
                {sessionCodes.map((code) => {
                  const t = fmtSessionTime(r.sessions?.[code])
                  const isBest = t.value != null && bestBySession[code] != null && t.value <= bestBySession[code] + 0.001
                  return (
                    <td key={code} className={`split num-col ${isBest ? 'purple' : ''} ${t.flagged ? 'flagged' : ''}`}>
                      {t.text}
                      {isRunPB(r.sessions, code, t.value, r.personalBest) && (
                        <span className="pb-pill">PB</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
